import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import type { UseFormSetValue, UseFormTrigger } from "react-hook-form";
import { type Translationtype } from "utils";
import { Tooltip } from "../components/tooltip";

type selectOptionsType = {
  label: string;
  value: string | number | any;
};

interface IFormSelectProps {
  register: any;
  errors: any;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  noTranslate?: boolean;
  name: string;
  title?: string;
  disabledOption?: number | string | any;
  defaultValue?: number | string | any;
  mapOptions?: (op: any) => void;
  tooltip?: string;
  labelTranslatePrefix?: string;
  options: selectOptionsType[] | any;
  onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  setSelectLabel?: Dispatch<SetStateAction<selectOptionsType>>;
  setValue?: UseFormSetValue<any>;
  trigger?: UseFormTrigger<any>;
  t: Translationtype;
  isRequired?: boolean;
  hideTitle?: boolean;
  customNoOptionText?: string;
}

export function FormSelect({
  register,
  errors,
  className,
  disabled,
  noTranslate = false,
  labelTranslatePrefix = "Options",
  options,
  name,
  mapOptions,
  disabledOption,
  defaultValue,
  tooltip,
  title,
  onChange,
  setSelectLabel,
  setValue,
  isRequired = false,
  hideTitle = false,
  t,
  customNoOptionText = undefined,
}: IFormSelectProps) {
  const formatOptions = (opt: any): selectOptionsType[] => {
    if (options && mapOptions) {
      return opt.map((item: any) => mapOptions(item));
    }
    return opt;
  };

  const _options = formatOptions(options);
  return (
    // @ts-expect-error
    <div onChange={onChange} className={`${className || ""} w-full`}>
      {!hideTitle ? (
        <label htmlFor={name} className="w-full block mb-1 text-textColor">
          {title} {isRequired ? <span className="text-red-500"> *</span> : null}{" "}
          {tooltip ? <Tooltip text={tooltip} /> : null}
        </label>
      ) : null}

      <select
        id={name}
        disabled={disabled || false}
        {...(defaultValue !== undefined ? { value: defaultValue } : { defaultValue: "" })}
        className={`${className || ""} select input w-full  cursor-pointer`}
        {...register(name, {
          onChange: (event) => {
            const target = event.target as HTMLSelectElement;
            const selectedValue = target.value;

            if (setSelectLabel) {
              const selectedOption = target.options[target.selectedIndex];
              setSelectLabel({
                label: selectedOption.text,
                value: selectedOption.value,
              });
            }

            // If you need validation immediately
            if (setValue) {
              setValue(name, selectedValue, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
          },
        })}
      >
        {_options?.length === 0 ? (
          <option disabled className="bg-gray-200">
            {t("Form.noSelectOptions")}
          </option>
        ) : (
          <>
            <option className="bg-gray-200" disabled value="">
              {customNoOptionText ?? t("Form.select")}
            </option>
            {_options.map((option, index) => {
              return (
                <option
                  value={option.value}
                  key={index}
                  disabled={disabledOption === option.value || false}
                  className={
                    disabledOption === option.value ? "bg-gray-200" : ""
                  }
                >
                  {noTranslate
                    ? option.label
                    : t(`${labelTranslatePrefix}.${option.label}` as any)}
                </option>
              );
            })}
          </>
        )}
      </select>

      {errors[`${name}`]?.message ? (
        <p className="text-sm my-3 text-error">
          {t(`${errors[`${name}`]?.message}` as any)}
        </p>
      ) : null}
    </div>
  );
}
